import React, { FC, memo, useEffect, useState } from 'react';

import {
  AnimationLevel,
  MiddleColumnContent,
  SettingsScreens,
  ThreadId,
} from '../../types';
import MessagesLayout from './MessagesLayout';
import Settings from './settings/Settings';
import useFoldersReducer from '../../hooks/reducers/useFoldersReducer';
import PaymentAi from '../payment/aiPurchase/PaymentAi';
import Transition from '../ui/Transition';
import CreateMediaSale from './mediasale/CreateMediaSale';
import CallsChat from '../left/Calls/CallsChat';
import FeedLayout from './feed/FeedLayout';
import {
  selectChat,
  selectChatBot,
  selectCurrentMessageList,
  selectIsChatBotNotStarted,
  selectIsInSelectMode,
  selectIsUserBlocked,
  selectTabState,
  selectThreadInfo,
} from '../../global/selectors';
import usePrevious from '../../hooks/usePrevious';
import {
  AiPrompt,
  IPayment,
  IWallet,
  IWithdrawTemplate,
  MessageListType,
} from '../../global/types';
import Statistics from './statistics/Statistics';
import QuickLinks from './QuickLinks';
import { FeedMiddleList } from '../main/Main';
import usePrevDuringAnimation from '../../hooks/usePrevDuringAnimation';
import {
  ANIMATION_END_DELAY,
  ANIMATION_LEVEL_MIN,
  SERVICE_NOTIFICATIONS_USER_ID,
  TMP_CHAT_ID,
} from '../../config';
import { getActions, withGlobal } from '../../global';

import './MiddleColumn.scss';
import {
  getCanPostInChat,
  getMessageSendingRestrictionReason,
  isChatChannel,
  isChatSuperGroup,
  isUserId,
} from '../../global/helpers';
import { ApiChatBannedRights, MAIN_THREAD_ID } from '../../api/types';
import { useTranslation } from 'react-i18next';
import useTimeout from '../../hooks/useTimeout';
import useSyncEffect from '../../hooks/useSyncEffect';
import useForceUpdate from '../../hooks/useForceUpdate';

interface OwnProps {
  isMobile?: boolean;
  handleCalls: (name: string) => void;
  feedMiddleScreen: FeedMiddleList;
  setFeedMiddleScreen: (screen: FeedMiddleList) => void;
}

type StateProps = {
  middleScreen: MiddleColumnContent;
  withdrawTemplate?: IWithdrawTemplate;
  wallet?: IWallet;
  chatId?: string;
  threadId?: ThreadId;
  canPost?: boolean;
  payment?: IPayment | undefined;
  messageListType?: MessageListType;
  canRestartBot?: boolean;
  canStartBot?: boolean;
  canSubscribe?: boolean;
  canUnblock?: boolean;
  isComments?: boolean;
  isPinnedMessageList?: boolean;
  shouldSendJoinRequest?: boolean;
  currentUserBannedRights?: ApiChatBannedRights;
  defaultBannedRights?: ApiChatBannedRights;
  isSelectModeActive?: boolean;
  shouldLoadFullChat?: boolean;
  shouldSkipHistoryAnimations?: boolean;
  animationLevel: AnimationLevel;
  currentTransitionKey: number;
};

const TRANSITION_RENDER_COUNT = Object.keys(MiddleColumnContent).length / 2;
const LAYER_ANIMATION_DURATION_MS = 450 + ANIMATION_END_DELAY;

const MiddleColumn: FC<OwnProps & StateProps> = ({
  handleCalls,
  middleScreen,
  withdrawTemplate,
  wallet,
  chatId,
  threadId,
  canPost,
  payment,
  isMobile,
  feedMiddleScreen,
  messageListType,
  canRestartBot,
  canStartBot,
  canSubscribe,
  canUnblock,
  isComments,
  isPinnedMessageList,
  shouldSendJoinRequest,
  currentUserBannedRights,
  defaultBannedRights,
  isSelectModeActive,
  shouldLoadFullChat,
  shouldSkipHistoryAnimations,
  animationLevel,
  currentTransitionKey,
  setFeedMiddleScreen,
}) => {
  const { clearWithdrawTemplate, setSettingsScreen, loadFullChat } =
    getActions();
  const { t, i18n } = useTranslation();
  const [foldersState, foldersDispatch] = useFoldersReducer();
  const [activePack, setActivePack] = useState<AiPrompt>();
  const prevScreen = usePrevious(middleScreen);

  const prevTransitionKey = usePrevious(currentTransitionKey);

  const { isReady, handleOpenEnd, handleSlideStop } = useIsReady(
    !shouldSkipHistoryAnimations && animationLevel !== ANIMATION_LEVEL_MIN,
    currentTransitionKey,
    prevTransitionKey,
    chatId,
    isMobile
  );

  const closeAnimationDuration = isMobile
    ? LAYER_ANIMATION_DURATION_MS
    : undefined;

  const renderingChatId = usePrevDuringAnimation(
    chatId,
    closeAnimationDuration
  );
  const renderingThreadId = usePrevDuringAnimation(
    threadId,
    closeAnimationDuration
  );
  const renderingMessageListType = usePrevDuringAnimation(
    messageListType,
    closeAnimationDuration
  );

  const renderingCanRestartBot = usePrevDuringAnimation(
    canRestartBot,
    closeAnimationDuration
  );
  const renderingCanStartBot = usePrevDuringAnimation(
    canStartBot,
    closeAnimationDuration
  );
  const renderingCanSubscribe = usePrevDuringAnimation(
    canSubscribe,
    closeAnimationDuration
  );
  const renderingCanUnblock = usePrevDuringAnimation(
    canUnblock,
    closeAnimationDuration
  );

  const renderingShouldSendJoinRequest = usePrevDuringAnimation(
    shouldSendJoinRequest,
    closeAnimationDuration
  );

  const withMessageListBottomShift = Boolean(
    renderingCanRestartBot ||
      renderingCanSubscribe ||
      renderingShouldSendJoinRequest ||
      renderingCanStartBot ||
      isPinnedMessageList
  );

  const messageSendingRestrictionReason = getMessageSendingRestrictionReason(
    t,
    currentUserBannedRights,
    defaultBannedRights
  );

  const composerRestrictionMessage = messageSendingRestrictionReason;

  const renderingCanPost =
    usePrevDuringAnimation(canPost, closeAnimationDuration) &&
    !renderingCanRestartBot &&
    !renderingCanStartBot &&
    !renderingCanSubscribe &&
    !renderingCanUnblock &&
    chatId !== TMP_CHAT_ID &&
    chatId !== SERVICE_NOTIFICATIONS_USER_ID;

  const isMessagingDisabled = Boolean(
    !isPinnedMessageList &&
      !renderingCanPost &&
      !renderingCanRestartBot &&
      !renderingCanStartBot &&
      !renderingCanSubscribe &&
      composerRestrictionMessage
  );

  const withExtraShift = Boolean(
    isMessagingDisabled || isSelectModeActive || isPinnedMessageList
  );

  useEffect(() => {
    if (prevScreen === MiddleColumnContent.Settings) {
      setSettingsScreen({ screen: SettingsScreens.Main });
      if (withdrawTemplate && wallet) {
        clearWithdrawTemplate({
          wallet_id: wallet.id,
          payment_id: withdrawTemplate.payment_id!,
        });
      }
    }
  }, [middleScreen]);

  useEffect(() => {
    if (chatId && shouldLoadFullChat && isReady) {
      loadFullChat({ chatId });
    }
  }, [shouldLoadFullChat, chatId, isReady, loadFullChat]);

  function renderContent() {
    switch (middleScreen) {
      case MiddleColumnContent.Settings:
        return (
          <Settings
            isActive={true}
            foldersState={foldersState}
            foldersDispatch={foldersDispatch}
            payment={payment}
            setActivePack={setActivePack}
          />
        );

      case MiddleColumnContent.Calls:
        return <CallsChat handleCalls={handleCalls} />;
      case MiddleColumnContent.PaymentAi:
        return (
          <PaymentAi
            prevMiddleScreen={prevScreen}
            activePack={activePack}
            setActivePack={setActivePack}
          />
        );
      case MiddleColumnContent.MediaSale:
        return <CreateMediaSale chatId='' />;
      case MiddleColumnContent.Feed:
        return (
          <FeedLayout
            isMobile={isMobile}
            feedScreen={feedMiddleScreen}
            setFeedScreen={setFeedMiddleScreen}
          />
        );
      case MiddleColumnContent.Statistics:
        return <Statistics chatId={chatId!} />;
      case MiddleColumnContent.MessageStatistics:
      // return (
      //   <MessageStatistics chatId={chatId!} isActive={isOpen && isActive} />
      // );
      case MiddleColumnContent.QuickLinks:
        return <QuickLinks />;
      default:
        return (
          <MessagesLayout
            chatId={renderingChatId!}
            threadId={renderingThreadId!}
            canPost={renderingCanPost!}
            isMobile={isMobile}
            isReady={isReady}
            isComments={isComments}
            prevMiddleScreen={prevScreen}
            withBottomShift={withMessageListBottomShift}
            renderingMessageListType={renderingMessageListType}
            renderingShouldSendJoinRequest={renderingShouldSendJoinRequest}
            withExtraShift={withExtraShift}
            isPinnedMessageList={isPinnedMessageList}
            isMessagingDisabled={isMessagingDisabled}
            isSelectModeActive={isSelectModeActive}
            composerRestrictionMessage={composerRestrictionMessage}
            renderingCanRestartBot={renderingCanRestartBot}
            renderingCanStartBot={renderingCanStartBot}
            prevTransitionKey={prevTransitionKey}
            currentTransitionKey={currentTransitionKey}
            shouldSkipHistoryAnimations={shouldSkipHistoryAnimations}
            animationLevel={animationLevel}
            handleOpenEnd={handleOpenEnd}
            handleSlideStop={handleSlideStop}
          />
        );
    }
  }

  return (
    <div id='MiddleColumn'>
      <Transition
        name='fade'
        activeKey={middleScreen}
        renderCount={TRANSITION_RENDER_COUNT}
        shouldCleanup
      >
        {renderContent()}
      </Transition>
    </div>
  );
};

export default memo(
  withGlobal((global): StateProps => {
    const { withdrawTemplate, wallets, payment } = global;

    const tabState = selectTabState(global);
    const currentMessageList = selectCurrentMessageList(global);

    const { middleScreen, shouldSkipHistoryAnimations, messageLists } =
      tabState;

    const state: StateProps = {
      middleScreen,
      withdrawTemplate,
      wallet: wallets && wallets.find((el) => el.type === 'main'),
      payment,
      isSelectModeActive: selectIsInSelectMode(global),
      shouldSkipHistoryAnimations,
      animationLevel: global.settings.byKey.animationLevel,
      currentTransitionKey: Math.max(0, messageLists.length - 1),
    };

    if (!currentMessageList) {
      return state;
    }

    const { chatId, threadId, type: messageListType } = currentMessageList;

    const chat = selectChat(global, chatId);
    const isPrivate = isUserId(chatId);

    const threadInfo = selectThreadInfo(global, chatId, threadId!);
    const isComments = Boolean(threadInfo?.originChannelId);
    const canPost = chat && getCanPostInChat(chat, threadId!, isComments);
    const isBotNotStarted = selectIsChatBotNotStarted(global, chatId);
    const bot = selectChatBot(global, chatId);
    const canRestartBot = Boolean(bot && selectIsUserBlocked(global, chatId));
    const canStartBot = !canRestartBot && isBotNotStarted;
    const isUserBlocked = isPrivate
      ? selectIsUserBlocked(global, chatId)
      : false;
    const canUnblock = isUserBlocked && !bot;
    const isMainThread =
      messageListType === 'thread' && threadId === MAIN_THREAD_ID;
    const isChannel = Boolean(chat && isChatChannel(chat));
    const canSubscribe = Boolean(
      chat &&
        isMainThread &&
        (isChannel || isChatSuperGroup(chat)) &&
        chat.isNotJoined &&
        !chat.joinRequests
    );

    const {
      isNotJoined,
      isJoinRequest,
      currentUserBannedRights,
      defaultBannedRights,
    } = chat || {};

    const isPinnedMessageList = messageListType === 'pinned';
    const shouldSendJoinRequest = Boolean(isNotJoined && isJoinRequest);

    return {
      ...state,
      chatId,
      threadId,
      messageListType,
      canRestartBot,
      canStartBot,
      canPost,
      canSubscribe,
      canUnblock,
      isComments,
      isPinnedMessageList,
      shouldSendJoinRequest,
      currentUserBannedRights,
      defaultBannedRights,
    };
  })(MiddleColumn)
);

function useIsReady(
  withAnimations?: boolean,
  currentTransitionKey?: number,
  prevTransitionKey?: number,
  chatId?: string,
  isMobile?: boolean
) {
  const [isReady, setIsReady] = useState(!isMobile);
  const forceUpdate = useForceUpdate();

  const willSwitchMessageList =
    prevTransitionKey !== undefined &&
    prevTransitionKey !== currentTransitionKey;
  if (willSwitchMessageList) {
    if (withAnimations) {
      setIsReady(false);
    } else {
      forceUpdate();
    }
  }

  useSyncEffect(() => {
    if (!withAnimations) {
      setIsReady(true);
    }
  }, [withAnimations]);

  useTimeout(() => {
    if (!isReady) {
      setIsReady(true);
    }
  }, LAYER_ANIMATION_DURATION_MS);

  function handleOpenEnd(e: React.TransitionEvent<HTMLDivElement>) {
    if (e.propertyName === 'transform' && e.target === e.currentTarget) {
      setIsReady(Boolean(chatId));
    }
  }

  function handleSlideStop() {
    setIsReady(true);
  }

  return {
    isReady: isReady && !willSwitchMessageList,
    handleOpenEnd: withAnimations ? handleOpenEnd : undefined,
    handleSlideStop: withAnimations ? handleSlideStop : undefined,
  };
}
